'use client';

import { useState, useEffect } from 'react';
import { IOrganization, IRepository, IUser } from '@/types';
import { IResponseError } from '@/lib/apiBase';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Icon from '@/ui/Icon';
import * as Yup from 'yup';
import Button from '@/ui/Button';
import { Row, Stack } from '@/ui/primatives';
import Form, { FieldSet, FormActions, ErrorMessage } from '@/components/Form';
import Input from '@/components/Input';

export type FormValues = {
  name?: string;
  organizationId?: string;
};

interface CreateRepositoryFormProps {
  defaultValues?: FormValues;
  user: IUser;
  organizations?: IOrganization[];
  repository: IRepository;
  error?: IResponseError;
  onSubmit: (values: FormValues) => void;
}

const formSchema = Yup.object().shape({
  organizationId: Yup.string(),
  name: Yup.string()
    .min(2, 'Must be at least 2 characters')
    .max(50)
    .matches(/^[a-zA-Z0-9-_.]+$/, 'only alphanumeric, dashes and underscores')
    .matches(/^[^_-].*/, 'cannot start with an underscore or dash'),
});

const RepositoryForkForm: React.FC<CreateRepositoryFormProps> = ({
  repository,
  organizations,
  user,
  error,
  onSubmit,
}) => {
  const {
    register,
    watch,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: { name: repository.name, organizationId: '' },
    resolver: yupResolver(formSchema),
  });

  const [errorMessage, setErrorMessage] = useState<string>();

  const organizationOptions =
    organizations?.map((org) => ({
      value: org.id,
      label: `${org.name} (Organization account)`,
    })) || [];

  const options = [{ value: '', label: `${user.username} (Personal account)` }, ...organizationOptions];

  const nameWatch = watch('name');
  const organizationIdWatch = watch('organizationId');

  useEffect(() => {
    const selectedNamespace = organizationIdWatch
      ? organizations?.find((org) => org.id === organizationIdWatch)?.name
      : user.username;

    if (selectedNamespace === repository.namespace && nameWatch === repository.name) {
      setErrorMessage('You cannot fork a repository with the same name in the same namespace.');
    } else {
      setErrorMessage(undefined);
    }
  }, [nameWatch, organizationIdWatch]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={20}>
        <p>By default, forks are named the same as their upstream repository. You can rename it if you choose.</p>
        <Row gap={10} alignItems="flex-end">
          <Input.Select name="organizationId" label="Owner" control={control} options={options} />
          <div style={{ fontSize: 20, paddingBottom: 10 }}>/</div>
          <Input name="name" label="Repository name" submit={register} error={errors.name} />
          <Button.Basic className="success" small>
            <span>CREATE FORK</span>
            {/* <Icon.ArrowRight /> */}
          </Button.Basic>
        </Row>
      </Stack>
      <FormActions>{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}</FormActions>
    </Form>
  );
};

export default RepositoryForkForm;
